import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { storeAddOnGroupSchema, type AddOnGroupFormValues } from '../schemas/addonGroup.schema';
import { Form, Input, Button, Label } from '@/shared/components';
import { AddOnGroupMemberSelect } from './AddOnGroupMemberSelect';
import type { AddOnGroupRecord } from '../services/addonGroup.types';

interface Props {
    initialValues?: Partial<AddOnGroupRecord>;
    onSubmit: (data: AddOnGroupFormValues) => void;
    isLoading?: boolean;
    submitLabel?: string;
}

export function AddOnGroupForm({ initialValues, onSubmit, isLoading = false, submitLabel = 'Save' }: Props) {
    const { handleSubmit, control, formState: { isSubmitting } } = useForm<AddOnGroupFormValues>({
        resolver: zodResolver(storeAddOnGroupSchema),
        defaultValues: {
            name:        initialValues?.name ?? '',
            description: initialValues?.description ?? null,
            addon_ids:   initialValues?.addons?.map((a) => a.id) ?? [],
        },
    });

    return (
        <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
            <div>
                <Label htmlFor="name">Name</Label>
                <Controller
                    name="name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input id="name" placeholder="Group name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                    )}
                />
            </div>
            <div>
                <Label htmlFor="description">Description <span className="text-mp-muted">(optional)</span></Label>
                <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input id="description" placeholder="Description" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                    )}
                />
            </div>
            <div>
                <Label>Member Optional Extras <span className="text-mp-muted">(optional)</span></Label>
                <Controller
                    name="addon_ids"
                    control={control}
                    render={({ field }) => (
                        <AddOnGroupMemberSelect
                            value={field.value ?? []}
                            onChange={field.onChange}
                            // Pass the group's existing members (which may
                            // include retired add-ons) so they remain visible
                            // and removable in edit mode — the inner picker
                            // otherwise only loads active add-ons. See #318.
                            existingMembers={initialValues?.addons ?? []}
                        />
                    )}
                />
            </div>
            <Button size="sm" disabled={isLoading || isSubmitting}>
                {isLoading || isSubmitting ? 'Saving...' : submitLabel}
            </Button>
        </Form>
    );
}
